// ─── MLB DATA TRANSFORMERS — pure functions, no React dependencies ────────────
// Raw MLB Stats API responses → app-ready shapes

import { findPlayerMatch } from "./betting";

// Coerce a team abbrev from adapter output into a short string or null. Guards
// the text `team` column: a non-string (object/array) would fail the game's
// atomic upsert, and an over-long value has no place in an abbrev column.
const abbrevOrNull = (v) => (typeof v === 'string' && v.length >= 1 && v.length <= 4 ? v : null);

// Extract all players from an MLB boxscore response into a flat lookup array
//
// Field names verified against a live MLB GUMBO feed (gamePk 777573, Tigers @
// Orioles, 2025-06-10, Final) on 2026-07-13. A player who did not bat/pitch
// has stats.batting / stats.pitching present as an EMPTY OBJECT {} -- not
// absent, not zero-filled. `singles` is not a payload field; it is derived.
// `era` is not present at the game-level stats.pitching (only in
// seasonStats.pitching), so it is guarded and will be null in current feeds.
export function parseBoxscorePlayers(data) {
  const players = [];
  for (const side of ['away', 'home']) {
    const boxscore = data?.liveData?.boxscore || data;
    const teamPlayers = boxscore?.teams?.[side]?.players;
    if (!teamPlayers || typeof teamPlayers !== 'object') continue;
    // Team abbrev lives in gameData.teams (the feed/live payload), not in
    // boxscore.teams[side].team (which only carries id/name).
    const teamAbbrev = abbrevOrNull(data?.gameData?.teams?.[side]?.abbreviation);
    for (const key of Object.keys(teamPlayers)) {
      const p = teamPlayers[key];
      const name = p?.person?.fullName;
      if (!name) continue;
      const batting = p?.stats?.batting;
      const pitching = p?.stats?.pitching;
      const didBat = !!(batting && Object.keys(batting).length > 0);
      const didPitch = !!(pitching && Object.keys(pitching).length > 0);
      players.push({
        // ── legacy keys — byte-identical names, values, source paths ──────
        // Type-guard the id (mirrors NHL): a non-numeric person.id must resolve
        // to null so the writer skips the row, never letting a bad value reach
        // the player_id bigint column and fail the whole game's atomic upsert.
        id: typeof p?.person?.id === 'number' ? p.person.id : null,
        name,
        team: teamAbbrev,
        hits: batting?.hits ?? 0,
        homeRuns: batting?.homeRuns ?? 0,
        rbi: batting?.rbi ?? 0,
        runs: batting?.runs ?? 0,
        totalBases: batting?.totalBases ?? 0,
        batterStrikeouts: batting?.strikeOuts ?? 0,
        pitchingStrikeouts: pitching?.strikeOuts ?? 0,
        // ── new batting fields — null when player did not bat ─────────────
        atBats: didBat ? (batting?.atBats ?? null) : null,
        singles: didBat
          ? (typeof batting?.singles === 'number'
              ? batting.singles
              : (batting?.hits ?? 0) - (batting?.doubles ?? 0) - (batting?.triples ?? 0) - (batting?.homeRuns ?? 0))
          : null,
        doubles: didBat ? (batting?.doubles ?? null) : null,
        triples: didBat ? (batting?.triples ?? null) : null,
        walks: didBat ? (batting?.baseOnBalls ?? null) : null,
        stolenBases: didBat ? (batting?.stolenBases ?? null) : null,
        // ── new pitching fields — null when player did not pitch ──────────
        inningsPitched: didPitch ? (pitching?.inningsPitched ?? null) : null,
        pitcherHitsAllowed: didPitch ? (pitching?.hits ?? null) : null,
        earnedRuns: didPitch ? (pitching?.earnedRuns ?? null) : null,
        pitcherWalks: didPitch ? (pitching?.baseOnBalls ?? null) : null,
        homeRunsAllowed: didPitch ? (pitching?.homeRuns ?? null) : null,
        era: didPitch ? (pitching?.era ?? null) : null,
        numberOfPitches: didPitch ? (pitching?.numberOfPitches ?? null) : null,
      });
    }
  }
  return players;
}

// Look up a player's live stats by full name (or numeric ID if available)
export function getPlayerStat(players, fullName, statType, legPlayerId) {
  const match = findPlayerMatch(players, fullName, legPlayerId);
  if (!match) return null;
  switch (statType) {
    case 'mlb:hits':
    case 'hits':
      return match.hits;
    case 'mlb:home_runs':
    case 'home_runs':
      return match.homeRuns;
    case 'mlb:rbis':
    case 'rbis':
      return match.rbi;
    case 'mlb:runs':
    case 'runs':
      return match.runs;
    case 'mlb:total_bases':
    case 'total_bases':
      return match.totalBases;
    case 'mlb:strikeouts_pitcher':
    case 'strikeouts_pitcher':
      return match.pitchingStrikeouts;
    case 'mlb:strikeouts_batter':
    case 'strikeouts_batter':
      return match.batterStrikeouts;
    default:
      return null;
  }
}
